import { blockchainApi } from './api'
import { NFTMetadata, NFTAttribute, BlockchainConfig } from '@/types'

export interface MintingOptions {
  name: string
  description: string
  image: string
  attributes: NFTAttribute[]
  externalUrl?: string
  blockchain: 'ethereum' | 'polygon' | 'solana'
  royaltyPercentage?: number
}

class BlockchainService {
  private readonly blockchainConfigs: Record<string, BlockchainConfig> = {
    ethereum: {
      network: 'ethereum',
      rpcUrl: import.meta.env.VITE_ETHEREUM_RPC_URL || 'https://mainnet.infura.io/v3/your_project_id',
      chainId: 1
    },
    polygon: {
      network: 'polygon',
      rpcUrl: import.meta.env.VITE_POLYGON_RPC_URL || 'https://polygon-rpc.com',
      chainId: 137
    },
    solana: {
      network: 'solana',
      rpcUrl: import.meta.env.VITE_SOLANA_RPC_URL || 'https://api.mainnet-beta.solana.com',
      chainId: 101
    }
  }

  /**
   * Create NFT metadata
   */
  createNFTMetadata(options: MintingOptions): NFTMetadata {
    return {
      name: options.name,
      description: options.description,
      image: options.image,
      attributes: options.attributes,
      externalUrl: options.externalUrl
    }
  }

  /**
   * Mint NFT on the specified blockchain
   */
  async mintNFT(contentId: string, options: MintingOptions): Promise<any> {
    try {
      const metadata = this.createNFTMetadata(options)
      
      const response = await blockchainApi.mintNFT(contentId, {
        metadata,
        blockchain: options.blockchain,
        royaltyPercentage: options.royaltyPercentage || 10
      })

      if (!response.success) {
        throw new Error(response.error || 'NFT minting failed')
      }

      return response.data
    } catch (error) {
      console.error('NFT minting error:', error)
      throw error
    }
  }

  /**
   * Get NFT information
   */
  async getNFT(tokenId: string): Promise<any> {
    try {
      const response = await blockchainApi.getNFT(tokenId)
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch NFT')
      }

      return response.data
    } catch (error) {
      console.error('Failed to get NFT:', error)
      throw error
    }
  }

  /**
   * Get user's NFTs
   */
  async getUserNFTs(params?: any): Promise<any> {
    try {
      const response = await blockchainApi.getUserNFTs(params)
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch NFTs')
      }

      return response.data
    } catch (error) {
      console.error('Failed to get user NFTs:', error)
      throw error
    }
  }

  /**
   * Get blockchain configuration
   */
  getBlockchainConfig(network: string): BlockchainConfig | undefined {
    return this.blockchainConfigs[network]
  }

  /**
   * Get supported blockchains
   */
  getSupportedBlockchains(): string[] {
    return Object.keys(this.blockchainConfigs)
  }

  /**
   * Validate blockchain network
   */
  isValidBlockchain(network: string): boolean {
    return network in this.blockchainConfigs
  }

  /**
   * Get network display name
   */
  getNetworkDisplayName(network: string): string {
    const names: Record<string, string> = {
      ethereum: 'Ethereum',
      polygon: 'Polygon',
      solana: 'Solana'
    }
    return names[network] || network
  }

  /**
   * Get network gas fees (estimated)
   */
  async getGasFees(network: string): Promise<any> {
    try {
      const config = this.getBlockchainConfig(network)
      if (!config) {
        throw new Error('Unsupported blockchain network')
      }

      // This would typically call an API to get current gas fees
      // For now, we'll return estimated values
      const estimates: Record<string, any> = {
        ethereum: {
          slow: { gasPrice: '20', gwei: '20' },
          medium: { gasPrice: '30', gwei: '30' },
          fast: { gasPrice: '50', gwei: '50' }
        },
        polygon: {
          slow: { gasPrice: '30', gwei: '30' },
          medium: { gasPrice: '50', gwei: '50' },
          fast: { gasPrice: '100', gwei: '100' }
        },
        solana: {
          slow: { fee: '0.000005' },
          medium: { fee: '0.00001' },
          fast: { fee: '0.00002' }
        }
      }

      return estimates[network] || estimates.ethereum
    } catch (error) {
      console.error('Failed to get gas fees:', error)
      throw error
    }
  }

  /**
   * Create royalty attributes
   */
  createRoyaltyAttributes(percentage: number): NFTAttribute[] {
    return [
      {
        trait_type: 'Royalty',
        value: `${percentage}%`
      },
      {
        trait_type: 'Platform',
        value: 'COSMK DRM'
      },
      {
        trait_type: 'Protected',
        value: 'Yes'
      }
    ]
  }

  /**
   * Create content type attributes
   */
  createContentTypeAttributes(contentType: string, duration?: number): NFTAttribute[] {
    const attributes: NFTAttribute[] = [
      {
        trait_type: 'Content Type',
        value: contentType
      }
    ]

    if (duration) {
      attributes.push({
        trait_type: 'Duration',
        value: `${Math.round(duration)}s`
      })
    }

    return attributes
  }
}

export const blockchainService = new BlockchainService()
export default blockchainService 